import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { Link } from "react-router-dom";
import { FAQ_CATEGORIES } from "../data/faq";

/**
 * Bottom-of-Products informational block.
 * Order: Lead Time strip → FAQ (primary) → Max Sizes table.
 * Dark theme to contrast the white product grid above.
 * Only Eurosteel-offered profiles are shown in the sizes table.
 */

const ease = [0.19, 1, 0.22, 1];

// Only the profiles Eurosteel actually offers.
// W50TB values sourced from the brochure MTS50 Thermal Break Steel row.
const SIZE_COLS = ["Application", "W20", "W40", "W50TB"];
const SIZE_ROWS = [
  ["Fixed", "5500 × 5500", "5500 × 5500", "5500 × 5500"],
  ["Swing", "900 × 2450", "900 × 2450", "900 × 2450"],
  ["Pivot", "1500 × 3600", "1500 × 3600", "1500 × 3600"],
  ["Barn", "1500 × 3600", "1500 × 3600", "1500 × 3600"],
  ["Lift & Slide", "1200 × 3000", "1200 × 3000", "1200 × 3000"],
  ["Bifold", "1200 × 2450", "1200 × 2450", "1200 × 2450"],
];

function CompactFAQ() {
  const [openId, setOpenId] = useState(null);

  return (
    <div data-testid="products-faq-section">
      <h2
        data-testid="products-faq-heading"
        className="font-serif-display text-4xl font-bold leading-[1.05] text-white lg:text-5xl"
      >
        Frequently asked.
      </h2>

      <div className="mt-10 grid gap-x-16 gap-y-2 lg:grid-cols-2">
        {FAQ_CATEGORIES.map((cat) => (
          <div key={cat.id} data-testid={`products-faq-cat-${cat.id}`} className="pt-6">
            <p className="mb-3 font-tech text-[11px] font-semibold uppercase tracking-[0.28em] text-[#c6a87c]">
              {cat.label}
            </p>
            <div className="divide-y divide-white/10 border-t border-white/10">
              {cat.items.map((item, i) => {
                const id = `${cat.id}-${i}`;
                const isOpen = openId === id;
                return (
                  <div key={id} data-testid={`products-faq-item-${cat.id}-${i}`}>
                    <button
                      type="button"
                      onClick={() => setOpenId(isOpen ? null : id)}
                      data-testid={`products-faq-toggle-${cat.id}-${i}`}
                      aria-expanded={isOpen}
                      className="group flex w-full items-start justify-between gap-4 py-5 text-left"
                    >
                      <span
                        className={`font-serif-display text-[17px] font-semibold leading-snug transition-colors lg:text-[19px] ${
                          isOpen ? "text-[#c6a87c]" : "text-white group-hover:text-[#c6a87c]"
                        }`}
                      >
                        {item.q}
                      </span>
                      <motion.span
                        animate={{ rotate: isOpen ? 45 : 0 }}
                        transition={{ duration: 0.4, ease }}
                        className={`mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border transition-colors ${
                          isOpen
                            ? "border-[#c6a87c] text-[#c6a87c]"
                            : "border-white/30 text-white/80 group-hover:border-[#c6a87c] group-hover:text-[#c6a87c]"
                        }`}
                      >
                        <Plus size={13} strokeWidth={1.75} />
                      </motion.span>
                    </button>
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          key="a"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.45, ease }}
                          className="overflow-hidden"
                        >
                          <p
                            data-testid={`products-faq-answer-${cat.id}-${i}`}
                            className="pb-5 pr-10 font-body text-[15px] font-medium leading-[1.75] text-white/85"
                          >
                            {item.a}
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-10 flex justify-end">
        <Link
          to="/faq"
          data-testid="products-faq-view-all"
          className="inline-flex items-center gap-2 font-tech text-[11px] font-semibold uppercase tracking-[0.28em] text-white transition-colors hover:text-[#c6a87c]"
        >
          View Full FAQ →
        </Link>
      </div>
    </div>
  );
}

function SizeTable() {
  return (
    <div data-testid="products-max-sizes-section">
      <h2 className="font-serif-display text-4xl font-bold leading-[1.05] text-white lg:text-5xl">
        Maximum sizes.
      </h2>

      {/* Desktop */}
      <div
        className="mt-10 hidden overflow-x-auto lg:block"
        data-testid="products-max-sizes-table-desktop"
      >
        <table className="w-full border-collapse font-body text-[15px]">
          <thead>
            <tr className="border-b border-white/25">
              {SIZE_COLS.map((c, i) => (
                <th
                  key={c}
                  className={`py-4 pr-6 text-left font-tech text-[11px] font-bold uppercase tracking-[0.28em] text-[#c6a87c] ${
                    i === 0 ? "w-[180px]" : ""
                  }`}
                >
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {SIZE_ROWS.map((row) => (
              <tr
                key={row[0]}
                data-testid={`size-row-${row[0].toLowerCase().replace(/\s|&/g, "")}`}
                className="border-b border-white/10"
              >
                {row.map((cell, ci) => (
                  <td
                    key={`${row[0]}-${ci}`}
                    className={`py-4 pr-6 ${
                      ci === 0
                        ? "font-tech text-[13px] font-bold uppercase tracking-[0.24em] text-white"
                        : "font-body text-[16px] font-semibold text-white/95"
                    }`}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile — stacked cards */}
      <div className="mt-8 space-y-3 lg:hidden" data-testid="products-max-sizes-cards-mobile">
        {SIZE_ROWS.map((row) => (
          <div
            key={row[0]}
            className="border border-white/15 bg-white/[0.03] p-5"
          >
            <p className="font-tech text-[12px] font-bold uppercase tracking-[0.26em] text-[#c6a87c]">
              {row[0]}
            </p>
            <dl className="mt-3 grid grid-cols-3 gap-3">
              {row.slice(1).map((v, i) => (
                <div key={SIZE_COLS[i + 1]}>
                  <dt className="font-tech text-[10px] font-bold uppercase tracking-[0.2em] text-white/60">
                    {SIZE_COLS[i + 1]}
                  </dt>
                  <dd className="mt-1 font-body text-[14px] font-semibold text-white">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>

      <p className="mt-6 font-body text-[13px] font-medium text-white/75">
        Dimensions in mm (width × height). Ask us about larger multi-panel screens.
      </p>
    </div>
  );
}

function LeadTimeStrip() {
  return (
    <div
      data-testid="products-lead-time-banner"
      className="flex flex-col items-start gap-5 border-y border-[#c6a87c]/40 bg-[#c6a87c]/[0.08] px-6 py-6 md:flex-row md:items-center md:justify-between md:px-8"
    >
      <p
        data-testid="products-lead-time-headline"
        className="font-serif-display text-xl font-bold leading-tight text-white md:text-2xl"
      >
        Current lead time —{" "}
        <span className="text-[#c6a87c]">approximately 16 weeks</span> from deposit &amp; signed drawings.
      </p>
      <Link
        to="/contact"
        data-testid="products-lead-time-cta"
        className="inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-full bg-[#c6a87c] px-7 py-3 font-tech text-[11px] font-bold uppercase tracking-[0.24em] text-[#0a0a0c] transition-all duration-500 hover:bg-[#d8bd93]"
      >
        Confirm My Timeline
      </Link>
    </div>
  );
}

export default function ProductsInfoSection() {
  return (
    <section
      data-testid="products-info-section"
      className="relative overflow-hidden bg-[#0a0a0c] text-white"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(198,168,124,0.10),transparent_55%),radial-gradient(circle_at_bottom_right,rgba(198,168,124,0.08),transparent_50%)]"
      />

      <div className="relative mx-auto max-w-[1400px] px-6 pb-24 pt-20 lg:px-16 lg:pb-32 lg:pt-28">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.7, ease }}
        >
          <LeadTimeStrip />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.9, ease }}
          className="mt-20 lg:mt-24"
        >
          <CompactFAQ />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.9, ease }}
          className="mt-24 lg:mt-28"
        >
          <SizeTable />
        </motion.div>
      </div>
    </section>
  );
}
